import clsx from 'clsx'
import React, { CSSProperties } from 'react'

import { Background } from '@Components/Background'
import { Container } from '@Components/Container'
import { Elements } from '@Components/Elements'
import { Elements as ElementsGraphQL } from '@Components/Elements/Types'
import { ImageV3 } from '@Components/Image'
import { RTEContent } from '@Components/RTEContent'
import { Button } from '@CoreComponents/Buttons/Button'
import { BlockAppearance, ReferenceContentFragment, RteField } from '@Generated/graphql'

import styles from './TilesCardV2.module.scss'

// Hint for the image loader / responsive sizes; mirrors the size CSS vars in TilesV2.module.scss.
const sizeToPx: Record<string, number> = {
  auto: 370,
  large: 370,
  medium: 300,
  small: 168,
  xsmall: 120,
}

// Padding around a stacked panel card (when a background is set), in CSS units.
const paddingToValue: Record<string, string> = {
  large: '28px',
  medium: '18px',
  none: '0',
  small: '12px',
}

export const TilesCardV2 = ({
  align = 'center',
  appearance,
  aspectRatio,
  buttonLabel,
  campaignTag,
  disableLinks = false,
  elements = null,
  image,
  layout,
  padding = 'none',
  showTitle = true,
  size,
  subTitle,
  title,
  titleLines = '1',
}: {
  align?: string
  appearance: BlockAppearance
  aspectRatio: string
  buttonLabel?: string
  campaignTag?: {
    appearance: BlockAppearance
    text: RteField
  }
  disableLinks?: boolean
  elements?: ElementsGraphQL[] | null
  image?: string
  layout: string
  padding?: string
  showTitle?: boolean
  size: string
  subTitle?: RteField | null
  title?: string
  titleLines?: string
}) => {
  const { asset, backgroundColor, textColor } = appearance

  const {
    asset: campaignTagAsset,
    backgroundColor: campaignTagBackgroundColor,
    textColor: campaignTagTextColor,
  } = campaignTag?.appearance || {}

  const sizes = `${sizeToPx[size] || sizeToPx.large}px`
  const hasText = showTitle && Boolean(title || subTitle)

  return (
    <div
      className={clsx(
        styles.card,
        styles[layout],
        styles[aspectRatio],
        styles[size],
        align === 'left' && styles.alignLeft,
        Boolean(backgroundColor) && styles.hasBackground,
      )}
      style={{
        '--tile-background-color': backgroundColor,
        '--tile-padding': paddingToValue[padding] || '0',
        '--tile-text-color': textColor,
        // a line count to clamp to, or 'none' to never clamp (both are valid -webkit-line-clamp values)
        '--tile-title-lines': titleLines,
      } as CSSProperties}
    >
      <div className={styles.imageWrap}>
        {asset && <Background asset={asset as ReferenceContentFragment} sizes={sizes} />}
        {!asset && image && (
          <ImageV3
            alt={title || ''}
            draggable={false}
            fill
            sizes={sizes}
            src={image}
            style={{ objectFit: 'cover' }}
          />
        )}
      </div>
      {elements && <Elements dimWithText disableLinks={disableLinks} elements={elements} />}
      <div className={clsx(styles.content, hasText && styles.withText)}>
        {campaignTag?.text && (
          <Container
            background={campaignTagAsset as ReferenceContentFragment}
            backgroundColor={campaignTagBackgroundColor || undefined}
            textColor={campaignTagTextColor || undefined}
            variant="minimal"
          >
            <RTEContent className={styles.campaignTag} data={campaignTag.text} />
          </Container>
        )}
        {showTitle && title && (<p className={styles.title}>{title}</p>)}
        {showTitle && subTitle && (<RTEContent className={styles.subTitle} data={subTitle} />)}
        {buttonLabel && (<Button className={styles.button} innerSpacing="medium" theme="transparent" type="button">{buttonLabel}</Button>)}
      </div>
    </div>
  )
}
